// @ts-nocheck
/**
 * ファンド詳細画面
 * 情報員NPC表示、価格チャート、購入・売却UI
 */

import React, { useState, useMemo } from 'react';
import { getFund, FUND_TYPES } from '../data/funds';
import { getInformant, getInformantComment } from '../data/informants';
import { saveInvestmentTradeToServer } from '../../../lib/supabase';
import { getFundPrice, getFundPriceChange, getFundPriceChangeFromBase } from '../systems/priceEngine';
import {
  validatePurchase,
  purchaseFund,
  validateSell,
  sellFund,
  getHolding,
  getHoldingValue,
  getUnlockedLeverages,
  getLeverageExpiryDays,
} from '../systems/fundManager';
import { INVESTMENT } from '../../data/constants';
import { useIsMobile } from '../../hooks/useMediaQuery';

// ファンドタイプ別の許可レバレッジ（将来変更可能なよう内部は制限しない）
const ALLOWED_LEVERAGES_BY_FUND_TYPE = {
  [FUND_TYPES.REGION]: [1, 2],           // 地域ファンド: 1, 2倍のみ
  [FUND_TYPES.META]: [1, 2, 5],          // メタファンド: 1, 2, 5倍まで
  [FUND_TYPES.CATEGORY]: [1, 2, 5],      // カテゴリファンド: 1, 2, 5倍まで
};

const FundDetail = ({
  fund,
  playerData,
  updatePlayerData,
  currentBattleNumber,
  marketIndex,
  marketState,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const [units, setUnits] = useState(1);
  const [mode, setMode] = useState('buy'); // 'buy' | 'sell'
  const [leverage, setLeverage] = useState(1);
  const [direction, setDirection] = useState('long'); // 'long' | 'short'
  const [stopLoss, setStopLoss] = useState('');  // SL%
  const [takeProfit, setTakeProfit] = useState('');  // TP%
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const investmentData = playerData.investmentData || {};

  // アンロック済みレバレッジをファンドタイプで制限
  const unlockedLeverages = useMemo(() => {
    const allUnlocked = getUnlockedLeverages(investmentData);
    const allowedForType = ALLOWED_LEVERAGES_BY_FUND_TYPE[fund.type];

    // ファンドタイプに応じた制限がある場合はフィルタ
    if (allowedForType) {
      return allUnlocked.filter(lev => allowedForType.includes(lev));
    }
    // 制限がない場合（未定義タイプ）は全て表示
    return allUnlocked;
  }, [investmentData, fund.type]);
  const informant = fund.informantId ? getInformant(fund.informantId) : null;
  // 新シグネチャで呼び出し、フォールバックとして後方互換シグネチャも使用
  const currentPrice = (marketIndex && marketState)
    ? getFundPrice(fund.id, marketIndex, marketState)
    : getFundPrice(investmentData, fund.id);
  const priceChange = getFundPriceChange(investmentData, fund.id);
  const priceChangeFromBase = (marketIndex && marketState)
    ? getFundPriceChangeFromBase(fund.id, marketIndex, marketState)
    : getFundPriceChangeFromBase(investmentData, fund.id);
  const holding = getHolding(investmentData, fund.id);
  const holdingValue = holding
    ? getHoldingValue(investmentData, fund.id, marketIndex, marketState)
    : null;

  // 価格履歴（チャート用）
  const priceHistory = investmentData.priceHistory?.[fund.id] || [];

  // 購入検証
  const purchaseValidation = useMemo(() => {
    if (mode !== 'buy') return null;
    return validatePurchase(investmentData, playerData.gold, fund.id, units, leverage, marketIndex, marketState);
  }, [investmentData, playerData.gold, fund.id, units, mode, leverage, marketIndex, marketState]);

  // 売却検証（保有IDを使用してレバレッジポジションも対応）
  const sellValidation = useMemo(() => {
    if (mode !== 'sell' || !holding) return null;
    return validateSell(investmentData, holding.id, units, marketIndex, marketState);
  }, [investmentData, holding, units, mode, marketIndex, marketState]);

  // 購入処理（サーバー優先）
  const handlePurchase = async () => {
    if (!purchaseValidation?.valid || isLoading) return;

    // SL/TPの数値変換
    const slValue = stopLoss !== '' ? parseFloat(stopLoss) : null;
    const tpValue = takeProfit !== '' ? parseFloat(takeProfit) : null;

    setIsLoading(true);

    try {
      // サーバー保存を先に実行
      const serverResult = await saveInvestmentTradeToServer({
        action: 'buy_fund',
        fund_id: fund.id,
        units,
        leverage,
        direction,
        stop_loss: slValue,
        take_profit: tpValue,
      });

      // 未認証の場合はローカルのみで処理
      if (serverResult.error === 'Not authenticated') {
        console.log('[FundDetail] Not authenticated, proceeding with local only');
      } else if (!serverResult.success) {
        // サーバーエラーの場合は処理を中止
        setMessage({
          type: 'error',
          text: `サーバー保存に失敗しました: ${serverResult.error}`,
        });
        return;
      } else {
        setMessage({ type: 'success', text: `${fund.name}を購入しました（手数料: ${serverResult.fee.toLocaleString()}G）` });
        setUnits(1); setLeverage(1); setDirection('long'); setStopLoss(''); setTakeProfit(''); return;
      }

      // ローカル処理
      const { newInvestmentData, cost, fee } = purchaseFund(
        investmentData,
        fund.id,
        units,
        currentBattleNumber,
        { leverage, direction, stopLoss: slValue, takeProfit: tpValue, marketIndex, marketState }
      );

      updatePlayerData((prev) => ({
        ...prev,
        gold: prev.gold - cost,
        investmentData: newInvestmentData,
      }));

      const leverageText = leverage > 1 ? `（${leverage}倍レバレッジ）` : '';
      const directionText = leverage > 1 && direction === 'short' ? '【ショート】' : '';
      const autoOrderText = (slValue || tpValue) ? ' [自動注文設定済]' : '';
      setMessage({
        type: 'success',
        text: `${directionText}${fund.name}を${units}口購入しました${leverageText}${autoOrderText}（手数料: ${fee.toLocaleString()}G）`,
      });
      setUnits(1);
      setLeverage(1);
      setDirection('long');
      setStopLoss('');
      setTakeProfit('');
    } finally {
      setIsLoading(false);
    }
  };

  // 売却処理（サーバー優先、FIFO方式で古いポジションから売却）
  const handleSell = async () => {
    if (!sellValidation?.valid || !holding || isLoading) return;

    setIsLoading(true);

    try {
      // サーバー保存を先に実行（FIFO方式: fund_id + sell_units）
      const serverResult = await saveInvestmentTradeToServer({
        action: 'sell_fund',
        fund_id: fund.id,
        sell_units: units,
      });

      // 未認証の場合はローカルのみで処理
      if (serverResult.error === 'Not authenticated') {
        console.log('[FundDetail] Not authenticated, proceeding with local only');
      } else if (!serverResult.success) {
        // サーバーエラーの場合は処理を中止
        setMessage({
          type: 'error',
          text: `サーバー保存に失敗しました: ${serverResult.error}`,
        });
        return;
      } else {
        setMessage({ type: 'success', text: `${fund.name}を売却しました（損益: ${serverResult.total_pnl.toLocaleString()}G）` });
        setUnits(1); setMode('buy'); return;
      }

      // ローカル処理
      const { newInvestmentData, revenue, fee, pnl } = sellFund(
        investmentData,
        holding.id,
        units,
        currentBattleNumber,
        marketIndex,
        marketState
      );

      updatePlayerData((prev) => ({
        ...prev,
        gold: prev.gold + revenue,
        investmentData: newInvestmentData,
      }));

      const pnlText = pnl >= 0 ? `+${pnl.toLocaleString()}G` : `${pnl.toLocaleString()}G`;
      setMessage({
        type: pnl >= 0 ? 'success' : 'warning',
        text: `${fund.name}を${units}口売却しました（損益: ${pnlText}）`,
      });
      setUnits(1);
      setMode('buy');
    } finally {
      setIsLoading(false);
    }
  };

  // スタイル
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      borderBottom: '1px solid #333',
      backgroundColor: '#16213e',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '12px',
    },
    backButton: {
      padding: '8px 16px',
      backgroundColor: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    fundTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    fundIcon: {
      fontSize: '32px',
    },
    fundName: {
      fontSize: isMobile ? '18px' : '20px',
      fontWeight: 'bold',
      color: '#e0e0e0',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px',
    },
    section: {
      marginBottom: '20px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#4fc3f7',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    informantSection: {
      padding: '12px',
      backgroundColor: 'rgba(79, 195, 247, 0.1)',
      borderRadius: '8px',
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start',
    },
    informantIcon: {
      fontSize: '28px',
    },
    informantContent: {
      flex: 1,
    },
    informantName: {
      fontWeight: 'bold',
      color: '#4fc3f7',
      marginBottom: '4px',
    },
    informantQuote: {
      fontSize: '14px',
      color: '#aaa',
      fontStyle: 'italic',
    },
    priceSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
    },
    priceCard: {
      padding: '12px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
      textAlign: 'center',
    },
    priceLabel: {
      fontSize: '12px',
      color: '#888',
      marginBottom: '4px',
    },
    priceValue: {
      fontSize: '18px',
      fontWeight: 'bold',
    },
    chartPlaceholder: {
      height: '120px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#666',
    },
    miniChart: {
      height: '80px',
      display: 'flex',
      alignItems: 'flex-end',
      gap: '2px',
      padding: '8px',
    },
    chartBar: {
      flex: 1,
      backgroundColor: '#4fc3f7',
      borderRadius: '2px 2px 0 0',
      minHeight: '4px',
    },
    holdingSection: {
      padding: '12px',
      backgroundColor: '#0f3460',
      borderRadius: '8px',
    },
    holdingRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '8px',
    },
    holdingLabel: {
      color: '#888',
    },
    holdingValue: {
      fontWeight: 'bold',
    },
    tradeSection: {
      padding: '16px',
      backgroundColor: '#16213e',
      borderRadius: '8px',
    },
    modeToggle: {
      display: 'flex',
      marginBottom: '16px',
      gap: '8px',
    },
    modeButton: {
      flex: 1,
      padding: '10px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: 'bold',
      transition: 'all 0.2s',
    },
    modeButtonActive: {
      backgroundColor: '#4fc3f7',
      color: '#000',
    },
    modeButtonInactive: {
      backgroundColor: '#333',
      color: '#888',
    },
    inputRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '12px',
    },
    inputLabel: {
      width: '60px',
      color: '#888',
    },
    input: {
      flex: 1,
      padding: '10px',
      backgroundColor: '#1a1a2e',
      border: '1px solid #333',
      borderRadius: '4px',
      color: '#e0e0e0',
      fontSize: '16px',
    },
    quickButtons: {
      display: 'flex',
      gap: '4px',
    },
    quickButton: {
      padding: '8px 12px',
      backgroundColor: '#333',
      border: 'none',
      borderRadius: '4px',
      color: '#e0e0e0',
      cursor: 'pointer',
      fontSize: '12px',
    },
    costDisplay: {
      padding: '12px',
      backgroundColor: '#1a1a2e',
      borderRadius: '4px',
      marginBottom: '12px',
    },
    costRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '4px',
    },
    costTotal: {
      fontWeight: 'bold',
      fontSize: '16px',
      borderTop: '1px solid #333',
      paddingTop: '8px',
      marginTop: '8px',
    },
    submitButton: {
      width: '100%',
      padding: '14px',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 'bold',
      fontSize: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    submitButtonEnabled: {
      backgroundColor: '#4fc3f7',
      color: '#000',
    },
    submitButtonDisabled: {
      backgroundColor: '#333',
      color: '#666',
      cursor: 'not-allowed',
    },
    errorText: {
      color: '#f44336',
      fontSize: '12px',
      marginTop: '8px',
    },
    messageBox: {
      padding: '12px',
      borderRadius: '8px',
      marginBottom: '12px',
    },
    messageSuccess: {
      backgroundColor: 'rgba(76, 175, 80, 0.2)',
      color: '#4caf50',
    },
    messageWarning: {
      backgroundColor: 'rgba(244, 67, 54, 0.2)',
      color: '#f44336',
    },
  };

  // 簡易チャート描画
  const renderMiniChart = () => {
    if (priceHistory.length < 2) {
      return <div style={styles.chartPlaceholder}>価格データ蓄積中...</div>;
    }

    const maxPrice = Math.max(...priceHistory);
    const minPrice = Math.min(...priceHistory);
    const range = maxPrice - minPrice || 1;

    return (
      <div style={styles.miniChart}>
        {priceHistory.slice(-20).map((price, i) => {
          const height = ((price - minPrice) / range) * 100;
          const isUp = i > 0 && price > priceHistory[priceHistory.length - 20 + i - 1];
          return (
            <div
              key={i}
              style={{
                ...styles.chartBar,
                height: `${Math.max(10, height)}%`,
                backgroundColor: isUp ? '#4caf50' : '#f44336',
              }}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* ヘッダー */}
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <button style={styles.backButton} onClick={onClose}>
            ← 戻る
          </button>
          <div style={styles.fundTitle}>
            <span style={styles.fundIcon}>{fund.icon}</span>
            <span style={styles.fundName}>{fund.name}</span>
          </div>
        </div>
      </div>

      {/* コンテンツ */}
      <div style={styles.content}>
        {/* メッセージ */}
        {message && (
          <div
            style={{
              ...styles.messageBox,
              ...(message.type === 'success' ? styles.messageSuccess : styles.messageWarning),
            }}
          >
            {message.text}
          </div>
        )}

        {/* 情報員セクション */}
        {informant && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span>🎭</span>
              <span>情報員レポート</span>
            </div>
            <div style={styles.informantSection}>
              <div style={styles.informantIcon}>{informant.icon}</div>
              <div style={styles.informantContent}>
                <div style={styles.informantName}>{informant.name}</div>
                <div style={styles.informantQuote}>
                  「{getInformantComment(fund.informantId, priceChange)}」
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 価格情報 */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span>📈</span>
            <span>価格情報</span>
          </div>
          <div style={styles.priceSection}>
            <div style={styles.priceCard}>
              <div style={styles.priceLabel}>現在価格</div>
              <div style={styles.priceValue}>{currentPrice.toLocaleString()}G/口</div>
            </div>
            <div style={styles.priceCard}>
              <div style={styles.priceLabel}>前日比</div>
              <div
                style={{
                  ...styles.priceValue,
                  color: priceChange >= 0 ? '#4caf50' : '#f44336',
                }}
              >
                {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(1)}%
              </div>
            </div>
            <div style={styles.priceCard}>
              <div style={styles.priceLabel}>基準価格比</div>
              <div
                style={{
                  ...styles.priceValue,
                  color: priceChangeFromBase >= 0 ? '#4caf50' : '#f44336',
                }}
              >
                {priceChangeFromBase >= 0 ? '+' : ''}{priceChangeFromBase.toFixed(1)}%
              </div>
            </div>
            <div style={styles.priceCard}>
              <div style={styles.priceLabel}>決済期限</div>
              <div style={styles.priceValue}>{INVESTMENT.MAX_HOLDING_DAYS}日</div>
            </div>
          </div>
        </div>

        {/* 簡易チャート */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span>📊</span>
            <span>価格推移</span>
          </div>
          {renderMiniChart()}
        </div>

        {/* 保有情報 */}
        {holding && holdingValue && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              <span>💼</span>
              <span>あなたの保有</span>
            </div>
            <div style={styles.holdingSection}>
              <div style={styles.holdingRow}>
                <span style={styles.holdingLabel}>保有口数</span>
                <span style={styles.holdingValue}>{holding.units}口</span>
              </div>
              <div style={styles.holdingRow}>
                <span style={styles.holdingLabel}>取得単価</span>
                <span style={styles.holdingValue}>{holding.purchasePrice.toLocaleString()}G</span>
              </div>
              <div style={styles.holdingRow}>
                <span style={styles.holdingLabel}>評価額</span>
                <span style={styles.holdingValue}>{holdingValue.value.toLocaleString()}G</span>
              </div>
              <div style={styles.holdingRow}>
                <span style={styles.holdingLabel}>評価損益</span>
                <span
                  style={{
                    ...styles.holdingValue,
                    color: holdingValue.pnl >= 0 ? '#4caf50' : '#f44336',
                  }}
                >
                  {holdingValue.pnl >= 0 ? '+' : ''}{holdingValue.pnl.toLocaleString()}G
                  ({holdingValue.pnlPercent >= 0 ? '+' : ''}{holdingValue.pnlPercent.toFixed(1)}%)
                </span>
              </div>
              <div style={styles.holdingRow}>
                <span style={styles.holdingLabel}>残り期間</span>
                <span style={styles.holdingValue}>
                  {Math.max(0, holding.expiryDate - currentBattleNumber)}日
                </span>
              </div>
            </div>
          </div>
        )}

        {/* 取引セクション */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <span>💰</span>
            <span>取引</span>
          </div>
          <div style={styles.tradeSection}>
            {/* モード切替 */}
            <div style={styles.modeToggle}>
              <button
                style={{
                  ...styles.modeButton,
                  ...(mode === 'buy' ? styles.modeButtonActive : styles.modeButtonInactive),
                }}
                onClick={() => { setMode('buy'); setUnits(1); }}
              >
                購入
              </button>
              <button
                style={{
                  ...styles.modeButton,
                  ...(mode === 'sell' ? styles.modeButtonActive : styles.modeButtonInactive),
                }}
                onClick={() => { setMode('sell'); setUnits(holding?.units || 1); }}
                disabled={!holding}
              >
                売却
              </button>
            </div>

            {/* 口数入力 */}
            <div style={styles.inputRow}>
              <span style={styles.inputLabel}>口数</span>
              <input
                type="number"
                min="1"
                max={mode === 'sell' ? holding?.units || 1 : INVESTMENT.MAX_UNITS_PER_FUND}
                value={units}
                onChange={(e) => setUnits(Math.max(1, parseInt(e.target.value) || 1))}
                style={styles.input}
              />
              <div style={styles.quickButtons}>
                <button style={styles.quickButton} onClick={() => setUnits(1)}>1</button>
                <button style={styles.quickButton} onClick={() => setUnits(5)}>5</button>
                <button style={styles.quickButton} onClick={() => setUnits(10)}>10</button>
                {mode === 'sell' && holding && (
                  <button style={styles.quickButton} onClick={() => setUnits(holding.units)}>全</button>
                )}
              </div>
            </div>

            {/* レバレッジ選択（購入時のみ） */}
            {mode === 'buy' && unlockedLeverages.length > 1 && (
              <>
                <div style={styles.inputRow}>
                  <span style={styles.inputLabel}>レバレッジ</span>
                  <div style={{ display: 'flex', gap: '4px', flex: 1, flexWrap: 'wrap' }}>
                    {unlockedLeverages.map((lev) => (
                      <button
                        key={lev}
                        style={{
                          ...styles.quickButton,
                          backgroundColor: leverage === lev ? '#4fc3f7' : '#333',
                          color: leverage === lev ? '#000' : '#e0e0e0',
                        }}
                        onClick={() => setLeverage(lev)}
                      >
                        {lev}x
                      </button>
                    ))}
                  </div>
                </div>

                {/* ロング/ショート選択（レバレッジ時のみ） */}
                {leverage > 1 && (
                  <div style={styles.inputRow}>
                    <span style={styles.inputLabel}>方向</span>
                    <div style={{ display: 'flex', gap: '8px', flex: 1 }}>
                      <button
                        style={{
                          ...styles.quickButton,
                          flex: 1,
                          backgroundColor: direction === 'long' ? '#4caf50' : '#333',
                          color: direction === 'long' ? '#fff' : '#888',
                        }}
                        onClick={() => setDirection('long')}
                      >
                        📈 ロング（上がる）
                      </button>
                      <button
                        style={{
                          ...styles.quickButton,
                          flex: 1,
                          backgroundColor: direction === 'short' ? '#f44336' : '#333',
                          color: direction === 'short' ? '#fff' : '#888',
                        }}
                        onClick={() => setDirection('short')}
                      >
                        📉 ショート（下がる）
                      </button>
                    </div>
                  </div>
                )}

                {/* レバレッジ警告 */}
                {leverage > 1 && (
                  <div
                    style={{
                      padding: '8px',
                      backgroundColor: 'rgba(255, 152, 0, 0.2)',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      fontSize: '12px',
                      color: '#ff9800',
                    }}
                  >
                    ⚠️ {leverage}倍レバレッジ: 利益・損失が{leverage}倍に拡大します。
                    <br />
                    📅 保有期限: {getLeverageExpiryDays(leverage)}日 | ロスカット: 証拠金維持率{(INVESTMENT.LIQUIDATION_THRESHOLD * 100).toFixed(0)}%未満
                  </div>
                )}
              </>
            )}

            {/* 自動注文（SL/TP）設定 - 購入時のみ */}
            {mode === 'buy' && (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: 'rgba(79, 195, 247, 0.1)',
                  borderRadius: '8px',
                  marginBottom: '12px',
                }}
              >
                <div style={{ fontSize: '12px', color: '#4fc3f7', marginBottom: '8px' }}>
                  🔧 自動注文（任意）
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                      🛡️ ストップロス（損切り）
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#888' }}>-</span>
                      <input
                        type="number"
                        min="1"
                        max="99"
                        placeholder="未設定"
                        value={stopLoss}
                        onChange={(e) => setStopLoss(e.target.value)}
                        style={{
                          ...styles.input,
                          flex: 1,
                          padding: '6px 8px',
                          fontSize: '14px',
                        }}
                      />
                      <span style={{ color: '#888' }}>%</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: '120px' }}>
                    <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                      🎯 テイクプロフィット（利確）
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#888' }}>+</span>
                      <input
                        type="number"
                        min="1"
                        max="999"
                        placeholder="未設定"
                        value={takeProfit}
                        onChange={(e) => setTakeProfit(e.target.value)}
                        style={{
                          ...styles.input,
                          flex: 1,
                          padding: '6px 8px',
                          fontSize: '14px',
                        }}
                      />
                      <span style={{ color: '#888' }}>%</span>
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                  ※日次更新時に条件達成で自動決済されます
                </div>
              </div>
            )}

            {/* コスト表示 */}
            <div style={styles.costDisplay}>
              {mode === 'buy' && purchaseValidation && (
                <>
                  {leverage > 1 && (
                    <div style={styles.costRow}>
                      <span>ポジション価値</span>
                      <span style={{ color: '#4fc3f7' }}>
                        {purchaseValidation.positionValue?.toLocaleString() || 0}G
                      </span>
                    </div>
                  )}
                  <div style={styles.costRow}>
                    <span>{leverage > 1 ? '必要証拠金' : '価格 × 口数'}</span>
                    <span>{purchaseValidation.subtotal?.toLocaleString() || 0}G</span>
                  </div>
                  <div style={styles.costRow}>
                    <span>手数料 (2%)</span>
                    <span>{purchaseValidation.fee?.toLocaleString() || 0}G</span>
                  </div>
                  <div style={{ ...styles.costRow, ...styles.costTotal }}>
                    <span>合計</span>
                    <span>{purchaseValidation.cost?.toLocaleString() || 0}G</span>
                  </div>
                  <div style={styles.costRow}>
                    <span>所持金</span>
                    <span>{playerData.gold.toLocaleString()}G</span>
                  </div>
                  {leverage > 1 && (
                    <div style={{ ...styles.costRow, marginTop: '8px', fontSize: '12px', color: '#888' }}>
                      <span>実効倍率</span>
                      <span style={{ color: leverage >= 10 ? '#f44336' : '#ff9800' }}>
                        {leverage}倍
                      </span>
                    </div>
                  )}
                </>
              )}
              {mode === 'sell' && sellValidation && (
                <>
                  {/* レバレッジポジションの場合は詳細表示 */}
                  {(sellValidation.leverage || 1) > 1 ? (
                    <>
                      <div style={styles.costRow}>
                        <span>ポジション価値</span>
                        <span style={{ color: '#888' }}>{sellValidation.gross?.toLocaleString() || 0}G</span>
                      </div>
                      <div style={styles.costRow}>
                        <span>証拠金（{sellValidation.leverage}倍）</span>
                        <span>{sellValidation.marginUsed?.toLocaleString() || 0}G</span>
                      </div>
                      <div style={styles.costRow}>
                        <span>ポジション損益</span>
                        <span style={{ color: (sellValidation.positionPnl || 0) >= 0 ? '#4caf50' : '#f44336' }}>
                          {(sellValidation.positionPnl || 0) >= 0 ? '+' : ''}
                          {sellValidation.positionPnl?.toLocaleString() || 0}G
                        </span>
                      </div>
                      <div style={styles.costRow}>
                        <span>手数料 (2%)</span>
                        <span>-{sellValidation.fee?.toLocaleString() || 0}G</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={styles.costRow}>
                        <span>売却額</span>
                        <span>{sellValidation.gross?.toLocaleString() || 0}G</span>
                      </div>
                      <div style={styles.costRow}>
                        <span>手数料 (2%)</span>
                        <span>-{sellValidation.fee?.toLocaleString() || 0}G</span>
                      </div>
                    </>
                  )}
                  <div style={{ ...styles.costRow, ...styles.costTotal }}>
                    <span>受取額</span>
                    <span>{sellValidation.revenue?.toLocaleString() || 0}G</span>
                  </div>
                  <div style={styles.costRow}>
                    <span>損益</span>
                    <span
                      style={{ color: (sellValidation.pnl || 0) >= 0 ? '#4caf50' : '#f44336' }}
                    >
                      {(sellValidation.pnl || 0) >= 0 ? '+' : ''}
                      {sellValidation.pnl?.toLocaleString() || 0}G
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* エラーメッセージ */}
            {mode === 'buy' && purchaseValidation && !purchaseValidation.valid && (
              <div style={styles.errorText}>{purchaseValidation.error}</div>
            )}
            {mode === 'sell' && sellValidation && !sellValidation.valid && (
              <div style={styles.errorText}>{sellValidation.error}</div>
            )}

            {/* 実行ボタン */}
            <button
              style={{
                ...styles.submitButton,
                ...(
                  !isLoading && (
                    (mode === 'buy' && purchaseValidation?.valid) ||
                    (mode === 'sell' && sellValidation?.valid)
                  )
                    ? styles.submitButtonEnabled
                    : styles.submitButtonDisabled
                ),
              }}
              onClick={mode === 'buy' ? handlePurchase : handleSell}
              disabled={
                isLoading ||
                (mode === 'buy' && !purchaseValidation?.valid) ||
                (mode === 'sell' && !sellValidation?.valid)
              }
            >
              {isLoading ? '処理中...' : (mode === 'buy' ? '購入する' : '売却する')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundDetail;
